import { Page, QueryBase } from "../../../shared/models/models";

// Enums
export enum OfferSortParameter {
  internalNumber = 1,
  createdDate = 2,
  customerName = 3,
}

export enum OfferStatusCode {
  draft = 1,
  sent = 2,
  viewed = 3,
  negotiating = 4,
  accepted = 5,
  rejected = 6,
  expired = 7,
  withdrawn = 8,
  awaitingConfirmation = 9,
  confirmed = 10,
  workInProgress = 11,
  workCompleted = 12,
  serviceCompleted = 13,
  archived = 14,
}

export enum CompletionTimeCode {
  sameDay = 1,
  oneToTwoDays = 2,
  threeToFiveDays = 3,
  oneWeek = 4,
  twoWeeks = 5,
  custom = 6,
}

export enum WarrantyPeriodCode {
  thirtyDays = 1,
  ninetyDays = 2,
  sixMonths = 3,
  oneYear = 4,
  custom = 5,
}

export enum OfferPaymentTermsCode {
  paymentOnCompletion = 1,
  net30Days = 2,
  fiftyAdvanceFiftyCompletion = 3,
  net60Days = 4,
  custom = 5,
}

export enum ValidityPeriodCode {
  threeDays = 1,
  fiveDays = 2,
  sevenDays = 3,
  fourteenDays = 4,
  thirtyDays = 5,
  custom = 6,
}

// Query interfaces
export interface OffersQuery extends QueryBase<OfferSortParameter> {
  statusCodes?: OfferStatusCode[];
  dateFrom?: string;
  dateTo?: string;
}

export interface OffersFiltersQuery {
  search?: string;
}

// Related entity interfaces
export interface OfferServiceRequest {
  id: string;
  internalNumber: string;
  issue: string;
  description?: string;
  statusCode?: number;
  serviceTypeCode?: number;
  priorityTypeCode?: number;
  openDate?: string;
  city?: string;
  countryName?: string;
  createdDate?: string;
  machine: OfferMachine;
}

export interface OfferMachine {
  id: string;
  name: string;
  brandName?: string;
  imageUrl?: string;
  catalogImageUrl?: string;
}

export interface OfferCustomer {
  id: string;
  name: string;
  logoUrl?: string;
  city?: string;
  countryName?: string;
  latitude?: string;
  longitude?: string;
}

export interface OfferFile {
  name: string;
  content: string;
  size: number;
  createdDate?: Date;
}

// Base offer interface (common fields)
export interface OfferBase {
  // Pricing
  laborCost?: number;
  laborHours?: number;
  hourlyRate?: number;
  partsCost?: number;
  materialDescription?: string;
  travelCost?: number;
  travelDistanceKm?: number;
  travelRatePerKm?: number;
  travelTimeHours?: number;
  travelRatePerHour?: number;
  additionalCosts?: number;
  additionalCostsDescription?: string;

  // Timeline
  earliestStartDate?: string;
  immediateStart: boolean;
  availableDates?: string;
  estimatedDuration?: string;
  completionTimeCode?: CompletionTimeCode;
  customCompletionTime?: string;

  // Service approach
  serviceApproach?: string;
  specialEquipmentRequired?: string;

  // Terms
  warrantyPeriodCode?: WarrantyPeriodCode;
  customWarrantyPeriod?: string;
  paymentTermsCode?: OfferPaymentTermsCode;
  customPaymentTerms?: string;

  // Included services
  includeDiagnostics: boolean;
  includeLaborForRepair: boolean;
  includePartsAndMaterials: boolean;
  includeTestingAndCalibration: boolean;
  includeWorkAreaCleanup: boolean;
  includeDetailedServiceReport: boolean;

  // Validity
  validityPeriodCode?: ValidityPeriodCode;
  customValidityPeriod?: string;

  // Customer message
  messageToCustomer?: string;

  // Legal confirmations
  confirmedBindingOffer: boolean;
  confirmedTermsOfService: boolean;
  confirmedAuthorityToOffer: boolean;

  // Notifications
  notifyOnViewAcceptReject: boolean;
  notifyTeamMembers: boolean;
}

// Full Offer interface (read-only response from API)
export interface Offer extends OfferBase {
  id: string;
  internalNumber: string;
  statusCode: OfferStatusCode;
  totalEstimatedCost: number;
  serviceRequest: OfferServiceRequest;
  customer: OfferCustomer;
  files?: OfferFile[];
  expiresAt?: string;
  sentDate?: string;
  viewedDate?: string;
  createdDate: string;
  modifiedDate: string;

  // Confirmation flow (populated after provider confirms)
  confirmedByProviderDate?: string;
  confirmedArrivalDate?: string;
  confirmedArrivalTimeFrom?: string;
  confirmedArrivalTimeTo?: string;
  confirmationDeadline?: string;
  confirmationMessage?: string;

  // Review data (populated when service completed and review exists)
  reviewRating?: number;
  reviewTitle?: string;
  reviewComment?: string;
  reviewDate?: string;

  // OC-Phase3: Execution tracking
  workStartedDate?: string;
  statusChangedDate?: string;
  progressUpdateCount?: number;
  lastProgressUpdateDate?: string;
}

// Create/Change interfaces
export interface ChangeOffer extends OfferBase {
  files?: OfferFile[];
}

export interface CreateOffer extends ChangeOffer {
  serviceRequestId: string;
}

// Filter response
export interface FilterParameter<TKey> {
  id: TKey;
  name?: string;
  value: number;
}

export interface OffersFilters {
  statusCodes: FilterParameter<OfferStatusCode>[];
}

// Stats (computed from filters endpoint)
export interface OfferStats {
  totalActiveOffers: number;
}

// State interface
export interface OffersState {
  offersQuery: OffersQuery;
  offers?: Page<Offer>;
  offersFilters?: OffersFilters;
  offerStats?: OfferStats;
  offer?: Offer;
  loading: boolean;
  success?: string;
  error: any;
}

// Form data interface (for React Hook Form)
export interface OfferFormData {
  // Pricing
  laborCost?: number;
  laborHours?: number;
  hourlyRate?: number;
  partsCost?: number;
  materialDescription?: string;
  travelCost?: number;
  travelDistanceKm?: number;
  travelRatePerKm?: number;
  travelTimeHours?: number;
  travelRatePerHour?: number;
  additionalCosts?: number;
  additionalCostsNote?: string;

  // Slider adjustments (frontend only, not sent to API)
  laborSliderAdjustment?: number;
  materialSliderAdjustment?: number;
  travelDistanceSliderAdjustment?: number;
  travelTimeSliderAdjustment?: number;
  additionalSliderAdjustment?: number;

  // Timeline
  earliestStartDate?: any; // Moment.js or Date
  immediateStart: boolean;
  availableDates?: string[];
  estimatedDuration?: string;
  completionTimeCode?: CompletionTimeCode;
  customCompletionTime?: string;

  // Service approach
  serviceApproach?: string;
  specialEquipmentRequired?: string;

  // Terms
  warrantyPeriodCode?: WarrantyPeriodCode;
  customWarrantyPeriod?: string;
  paymentTermsCode?: OfferPaymentTermsCode;
  customPaymentTerms?: string;

  // Included services
  includeDiagnostics: boolean;
  includeLaborForRepair: boolean;
  includePartsAndMaterials: boolean;
  includeTestingAndCalibration: boolean;
  includeWorkAreaCleanup: boolean;
  includeDetailedServiceReport: boolean;

  // Validity
  validityPeriodCode?: ValidityPeriodCode;
  customValidityPeriod?: string;

  // Customer message
  messageToCustomer?: string;

  // Legal confirmations
  confirmedBindingOffer: boolean;
  confirmedTermsOfService: boolean;
  confirmedAuthorityToOffer: boolean;

  // Notifications
  notifyOnViewAcceptReject: boolean;
  notifyTeamMembers: boolean;

  // Files
  files?: OfferFile[];
}
